'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image'; // ALLOW-RAW-IMG: OutcomePill is a generic primitive — outcome avatar URLs come from arbitrary origins (data:, IPFS, external CDNs, static `/team/*`). NSFW gating + entity lifecycle is the caller's concern; threading <OptimizedImage>'s entity-shaped props would couple the primitive to user-content moderation logic that doesn't belong here.
import type { Context, MouseEvent, ReactNode } from 'react';
import { createContext, use } from 'react';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import type { OutcomeHueName } from '@/lib/chart/outcome-hue';
import { cn } from '@/lib/ui/utils';
import { useLightboxStore } from '@/stores/lightbox';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type OutcomePillSize = 'xs' | 'sm' | 'md' | 'lg';

/**
 * `compact` — cap label width + ellipsis (dense rows, horizontal scroll).
 * `natural` — label uses available width and wraps; use in flex-wrap stacks (e.g. activity feed).
 */
export type OutcomePillLabelLayout = 'compact' | 'natural';
export interface OutcomePillProps {
  /** Explicit outcome hue. Mutually exclusive with `outcome`. */
  hue?: OutcomeHueName | undefined;
  /** Outcome identity. Hue derived via `useOutcomeColor` (Yes/No reserved). */
  outcome?: {
    title: string;
    index: number;
    marketPubkey?: string | undefined;
  } | undefined;
  /** Size scale. Default `'xs'` (Figma 677:621 reference). */
  size?: OutcomePillSize | undefined;
  /** Image URL rendered as a 20px avatar circle. Only honored at `size="md"`. */
  image?: string | undefined;
  /** Alt text for `image`. Defaults to empty string (decorative — label carries semantics). */
  imageAlt?: string | undefined;
  /** When true, clicking the image opens it in the global lightbox. Only honored when `image` is set. */
  imageClickable?: boolean | undefined;
  /** Flat convenience: rendered when no `children`. */
  label?: ReactNode | undefined;
  /** Flat convenience: rendered when no `children`. */
  value?: ReactNode | undefined;
  /** How the label slot handles long text. Default `compact`. */
  labelLayout?: OutcomePillLabelLayout | undefined;
  /** Compound form. When set, wins over flat `label`/`value`. */
  children?: ReactNode | undefined;
  /** Layout passthrough; merged via cn() after intrinsic root classes. */
  className?: string | undefined;
}
export interface OutcomePillDotProps {
  className?: string | undefined;
}
export interface OutcomePillLabelProps {
  children?: ReactNode | undefined;
  className?: string | undefined;
}
export interface OutcomePillValueProps {
  children?: ReactNode | undefined;
  className?: string | undefined;
}
export interface OutcomePillImageProps {
  src: string;
  alt?: string | undefined;
  className?: string | undefined;
  /** When provided, the image renders as a button that fires this handler on click. */
  onClick?: (() => void) | undefined;
}

// ---------------------------------------------------------------------------
// Token tables (module-level, React Compiler friendly)
// ---------------------------------------------------------------------------

const ROOT_BASE = 'inline-flex items-center rounded-pill select-none cursor-default' as const;

// `md` vertical padding is `py-[3px]` for both branches (aligned with `StatusPill`).
// With-image uses asymmetric `pl-[3px] pr-2`; without-image uses symmetric `px-3`.
const ROOT_SIZE: Record<OutcomePillSize, string> = {
  xs: 'gap-1 px-1.5 py-1',
  sm: 'gap-1.5 px-2 py-1.5',
  md: 'gap-1.5 px-3 py-[3px]',
  lg: 'gap-2 px-3.5 py-1.5'
};
const ROOT_SIZE_MD_WITH_IMAGE = 'gap-1.5 pl-[3px] pr-2 py-[3px]' as const;
// At `lg` the image is 28px so the left padding shrinks proportionally to
// keep the avatar visually flush with the pill's rounded edge.
const ROOT_SIZE_LG_WITH_IMAGE = 'gap-2 pl-1 pr-3 py-1' as const;
const DOT_SIZE: Record<OutcomePillSize, string> = {
  xs: 'size-1.5 shrink-0 rounded-full',
  sm: 'size-2 shrink-0 rounded-full',
  md: 'size-2 shrink-0 rounded-full',
  lg: 'size-2.5 shrink-0 rounded-full'
};
const TEXT_SIZE: Record<OutcomePillSize, string> = {
  xs: 'text-micro leading-[10px] font-medium',
  sm: 'text-caption leading-tight font-medium',
  md: 'text-caption leading-tight font-medium tracking-[-0.14px]',
  // ARBITRARY: tracking matches Figma 3333:2398
  lg: 'text-body leading-tight font-medium'
};
const TEXT_NOWRAP = 'whitespace-nowrap' as const;

// Per-size cap on the LABEL slot only — long outcome names truncate with `…`
// so the value (percent) stays whole and the pill stays compact in
// horizontal-scroll containers. Pixel values are out-of-scale because the
// pill's text is also out-of-scale (10px / 12px) — Tailwind spacing tokens
// don't have a matching cap that pairs with these text sizes.
const LABEL_MAX_WIDTH: Record<OutcomePillSize, string> = {
  xs: 'max-w-[40px] truncate',
  // ARBITRARY: tight cap at 10px text — keeps the pill compact in horizontal-scroll rows; long outcome names ellipsis-cut
  sm: 'max-w-[56px] truncate',
  // ARBITRARY: same tight-cap rationale at sm size
  md: 'max-w-[120px] truncate',
  // ARBITRARY: wider cap for body-text labels (proper nouns like "Pep Guardiola")
  lg: 'max-w-[160px] truncate' // ARBITRARY: wider cap for the 14px lg pill used in the proposal-outcomes panel — keeps multi-word labels readable
};

// Image-variant sizes for the leading avatar. Only `md` and `lg` allow
// images; the wrapper class adjusts to match `--avatar-px`.
const IMAGE_SIZE_PX: Record<'md' | 'lg', number> = {
  md: 20,
  lg: 28
};
const IMAGE_WRAPPER_SIZE: Record<'md' | 'lg', string> = {
  md: 'size-5',
  lg: 'size-7'
};
const VALUE_EXTRA = 'font-mono tabular-nums' as const;

// ---------------------------------------------------------------------------
// Internal context — carries hue + size from root to subcomponents
// ---------------------------------------------------------------------------

interface OutcomePillContextValue {
  hue: OutcomeHueName;
  size: OutcomePillSize;
  labelLayout: OutcomePillLabelLayout;
}
const OutcomePillContext: Context<OutcomePillContextValue | null> = createContext<OutcomePillContextValue | null>(null);
function useOutcomePillContext(): OutcomePillContextValue {
  const ctx = use(OutcomePillContext);
  if (ctx === null) {
    throw new Error('OutcomePill subcomponents must be rendered inside <OutcomePill>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Subcomponents (named exports + dot-namespace sugar attached at module bottom)
// ---------------------------------------------------------------------------

export function OutcomePillDot(t0) {
  const $ = _c(6);
  const {
    className
  } = t0;
  const {
    hue,
    size
  } = useOutcomePillContext();
  const t1 = DOT_SIZE[size];
  const t2 = `bg-outcome-${hue}-text`;
  let t3;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2) {
    t3 = cn(t1, t2, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span aria-hidden="true" className={t3} />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export function OutcomePillLabel(t0) {
  const $ = _c(8);
  const {
    children,
    className
  } = t0;
  const {
    hue,
    size,
    labelLayout
  } = useOutcomePillContext();
  let t1;
  if ($[0] !== className || $[1] !== hue || $[2] !== labelLayout || $[3] !== size) {
    const flowClass = labelLayout === "natural" ? "min-w-0 max-w-full whitespace-normal break-words [overflow-wrap:anywhere]" : cn(TEXT_NOWRAP, LABEL_MAX_WIDTH[size]);
    t1 = cn(TEXT_SIZE[size], flowClass, `text-outcome-${hue}-text`, className);
    $[0] = className;
    $[1] = hue;
    $[2] = labelLayout;
    $[3] = size;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== children || $[6] !== t1) {
    t2 = <span className={t1}>{children}</span>;
    $[5] = children;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function OutcomePillValue(t0) {
  const $ = _c(7);
  const {
    children,
    className
  } = t0;
  const {
    hue,
    size
  } = useOutcomePillContext();
  const t1 = TEXT_SIZE[size];
  const t2 = `text-outcome-${hue}-text`;
  let t3;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2) {
    t3 = cn(t1, TEXT_NOWRAP, VALUE_EXTRA, t2, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== t3) {
    t4 = <span className={t3}>{children}</span>;
    $[4] = children;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
export function OutcomePillImage(t0) {
  const $ = _c(21);
  const {
    src,
    alt,
    className,
    onClick
  } = t0;
  const {
    hue,
    size
  } = useOutcomePillContext();
  if (size !== "md" && size !== "lg") {
    throw new Error("OutcomePill.Image: only supported at size=\"md\" or \"lg\"");
  }
  const imagePx = IMAGE_SIZE_PX[size];
  const t1 = IMAGE_WRAPPER_SIZE[size];
  const t2 = `bg-outcome-${hue}-cta`;
  const t3 = `border-outcome-${hue}-text`;
  const t4 = onClick !== undefined && "cursor-zoom-in transition-opacity hover:opacity-80";
  let t5;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = cn(t1, "shrink-0 overflow-hidden rounded-full border-[1.2px]", t2, t3, t4, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const wrapperClass = t5;
  const t6 = alt ?? "";
  let t7;
  if ($[6] !== imagePx || $[7] !== src || $[8] !== t6) {
    t7 = <Image alt={t6} className="size-full object-cover" height={imagePx} src={src} unoptimized={true} width={imagePx} />;
    $[6] = imagePx;
    $[7] = src;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const inner = t7;
  if (onClick !== undefined) {
    let t8;
    if ($[10] !== onClick) {
      t8 = e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      };
      $[10] = onClick;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    const t9 = alt !== undefined && alt !== "" ? alt : undefined;
    let t10;
    if ($[12] !== inner || $[13] !== t8 || $[14] !== t9 || $[15] !== wrapperClass) {
      t10 = <button type="button" onClick={t8} aria-label={t9} className={wrapperClass}>{inner}</button>;
      $[12] = inner;
      $[13] = t8;
      $[14] = t9;
      $[15] = wrapperClass;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    return t10;
  }
  const t8 = alt === undefined || alt === "" ? "true" : undefined;
  let t9;
  if ($[17] !== inner || $[18] !== t8 || $[19] !== wrapperClass) {
    t9 = <span aria-hidden={t8} className={wrapperClass}>{inner}</span>;
    $[17] = inner;
    $[18] = t8;
    $[19] = wrapperClass;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

const FALLBACK_OUTCOME = {
  title: '',
  index: 0
} as const;
function OutcomePillImpl(t0) {
  const $ = _c(27);
  const {
    hue,
    outcome,
    size: t1,
    image,
    imageAlt,
    imageClickable,
    label,
    value,
    labelLayout: t2,
    children,
    className
  } = t0;
  const size = t1 === undefined ? "xs" : t1;
  const labelLayout = t2 === undefined ? "compact" : t2;
  const t3 = outcome?.marketPubkey;
  let t4;
  if ($[0] !== t3) {
    t4 = {
      marketPubkey: t3
    };
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const derived = useOutcomeColor(outcome ?? FALLBACK_OUTCOME, t4);
  if (hue === undefined && outcome === undefined) {
    throw new Error("OutcomePill: requires either `hue` or `outcome` prop");
  }
  if (image !== undefined && size !== "md" && size !== "lg") {
    throw new Error("OutcomePill: `image` is only supported at size=\"md\" or \"lg\"");
  }
  const resolvedHue = hue ?? derived.hue;
  const hasImage = image !== undefined && (size === "md" || size === "lg");
  const rootSizeClasses = hasImage ? size === "lg" ? ROOT_SIZE_LG_WITH_IMAGE : ROOT_SIZE_MD_WITH_IMAGE : ROOT_SIZE[size];
  let t5;
  if ($[2] !== hasImage || $[3] !== image || $[4] !== imageAlt || $[5] !== imageClickable) {
    t5 = hasImage ? <OutcomePillImage src={image} alt={imageAlt} onClick={imageClickable === true ? () => {
      useLightboxStore.getState().openLightbox([{
        src: image,
        alt: imageAlt ?? ""
      }]);
    } : undefined} /> : <OutcomePillDot />;
    $[2] = hasImage;
    $[3] = image;
    $[4] = imageAlt;
    $[5] = imageClickable;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const leading = t5;
  let t6;
  if ($[7] !== children || $[8] !== label || $[9] !== leading || $[10] !== value) {
    t6 = children !== undefined ? children : <>{leading}{label !== undefined ? <OutcomePillLabel>{label}</OutcomePillLabel> : null}{value !== undefined ? <OutcomePillValue>{value}</OutcomePillValue> : null}</>;
    $[7] = children;
    $[8] = label;
    $[9] = leading;
    $[10] = value;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const content = t6;
  let t7;
  if ($[12] !== labelLayout || $[13] !== resolvedHue || $[14] !== size) {
    t7 = {
      hue: resolvedHue,
      size,
      labelLayout
    };
    $[12] = labelLayout;
    $[13] = resolvedHue;
    $[14] = size;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = labelLayout === "natural" && "max-w-full items-start";
  const t9 = `bg-outcome-${resolvedHue}-outline`;
  let t10;
  if ($[16] !== className || $[17] !== rootSizeClasses || $[18] !== t8 || $[19] !== t9) {
    t10 = cn(ROOT_BASE, t8, rootSizeClasses, t9, className);
    $[16] = className;
    $[17] = rootSizeClasses;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== content || $[22] !== t10) {
    t11 = <span className={t10}>{content}</span>;
    $[21] = content;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t7) {
    t12 = <OutcomePillContext.Provider value={t7}>{t11}</OutcomePillContext.Provider>;
    $[24] = t11;
    $[25] = t7;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}

// Dot-namespace sugar — `<OutcomePill.Dot />` works alongside named imports.
interface OutcomePillType {
  (props: OutcomePillProps): React.ReactNode;
  Dot: typeof OutcomePillDot;
  Image: typeof OutcomePillImage;
  Label: typeof OutcomePillLabel;
  Value: typeof OutcomePillValue;
}
export const OutcomePill: OutcomePillType = Object.assign(OutcomePillImpl, {
  Dot: OutcomePillDot,
  Image: OutcomePillImage,
  Label: OutcomePillLabel,
  Value: OutcomePillValue
});